import React from 'react';

/** Match canvas backing store to element CSS size for crisp overlay drawing. */
export function useCanvasLayout(
    canvasRef: React.RefObject<HTMLCanvasElement | null>,
    containerRef: React.RefObject<HTMLElement | null>,
    onAfterResize?: () => void
) {
    /** Avoid infinite loops: notify only when layout-relevant values change (not on every resize() call). */
    const lastLayoutRef = React.useRef<{w: number; h: number; dpr: number}>({
        w: -1,
        h: -1,
        dpr: -1,
    });

    const resize = React.useCallback(() => {
        const canvas = canvasRef.current;
        const box = containerRef.current;
        if (!canvas || !box) {
            return;
        }
        const w = box.clientWidth;
        const h = box.clientHeight;
        if (w === 0 || h === 0) {
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        const bw = Math.max(1, Math.floor(w * dpr));
        const bh = Math.max(1, Math.floor(h * dpr));
        if (canvas.width !== bw || canvas.height !== bh) {
            canvas.width = bw;
            canvas.height = bh;
        }
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
        const prev = lastLayoutRef.current;
        if (prev.w !== w || prev.h !== h || prev.dpr !== dpr) {
            lastLayoutRef.current = {w, h, dpr};
            onAfterResize?.();
        }
    }, [canvasRef, containerRef, onAfterResize]);

    React.useLayoutEffect(() => {
        resize();
    }, [resize]);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const ro = new ResizeObserver(() => resize());
        ro.observe(el);
        const onWin = () => resize();
        window.addEventListener('resize', onWin);
        document.addEventListener('fullscreenchange', onWin);
        window.addEventListener('orientationchange', onWin);
        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener('resize', onWin);
            vv.addEventListener('scroll', onWin);
        }
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onWin);
            document.removeEventListener('fullscreenchange', onWin);
            window.removeEventListener('orientationchange', onWin);
            if (vv) {
                vv.removeEventListener('resize', onWin);
                vv.removeEventListener('scroll', onWin);
            }
        };
    }, [resize]);

    return {resize};
}
